/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { commonReducers, CommonState } from '../_common/commonReducers';

export interface Weighting {
 Id: number;
 KescId: number;
 Weighter: string;
 WeighterGuid: number;
 WeighingDate: string;
 UserName: string;
 WeighingType: string;
 CarrierId: string;
 CargoId: string;
 Speed: number;
 Disbalance: number;
 AdditionalInfo: string;
 Modified: string;
 Weight: number;
}

export interface WeightingPicture {
 Id: number;
 PicData: number[];
 PicWhen: string;
 Type: string;
}

interface WeightingsState extends CommonState {
 weightings: Weighting[];
 weightingPictures: WeightingPicture[];
}

const initialState: WeightingsState = {
 items: [],
 aggregate: null,
 item: null,
 structure: [],
 documents: [],
 rowCount: '0',
 loading: {},
 success: {},
 weightings: [],
 weightingPictures: [],
 excel: null,
};

const weightingsSlice = createSlice({
 name: 'weightings',
 initialState,
 reducers: {
  ...commonReducers,

  // Get weighting by ID
  getWeightingStart(state, action: PayloadAction<number>) {
   return {
    ...state,
    loading: { ...state.loading, getWeighting: true },
   };
  },
  getWeightingSuccess(state, action: PayloadAction<Weighting>) {
   return {
    ...state,
    item: action.payload,
    success: { ...state.success, getWeighting: true },
    loading: { ...state.loading, getWeighting: null },
   };
  },
  getWeightingFailure(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, getWeighting: false },
    loading: { ...state.loading, getWeighting: null },
   };
  },

  // Create weighting
  createWeightingStart(state, action: PayloadAction<Partial<Weighting>>) {
   return {
    ...state,
    loading: { ...state.loading, createWeighting: true },
   };
  },
  createWeightingSuccess(state, action: PayloadAction<Weighting>) {
   return {
    ...state,
    weightings: [...state.weightings, action.payload],
    success: { ...state.success, createWeighting: true },
    loading: { ...state.loading, createWeighting: null },
   };
  },
  createWeightingFailure(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, createWeighting: false },
    loading: { ...state.loading, createWeighting: null },
   };
  },

  // Update weighting
  updateWeightingStart(
   state,
   action: PayloadAction<{ id: number; data: Partial<Weighting> }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, updateWeighting: true },
   };
  },
  updateWeightingSuccess(state, action: PayloadAction<Weighting>) {
   return {
    ...state,
    weightings: state.weightings.map((w) =>
     w.Id === action.payload.Id ? action.payload : w,
    ),
    success: { ...state.success, updateWeighting: true },
    loading: { ...state.loading, updateWeighting: null },
   };
  },
  updateWeightingFailure(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, updateWeighting: false },
    loading: { ...state.loading, updateWeighting: null },
   };
  },

  // Delete weighting
  deleteWeightingStart(state, action: PayloadAction<number>) {
   return {
    ...state,
    loading: { ...state.loading, deleteWeighting: true },
   };
  },
  deleteWeightingSuccess(state, action: PayloadAction<number>) {
   return {
    ...state,
    weightings: state.weightings.filter((w) => w.Id !== action.payload),
    success: { ...state.success, deleteWeighting: true },
    loading: { ...state.loading, deleteWeighting: null },
   };
  },
  deleteWeightingFailure(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, deleteWeighting: false },
    loading: { ...state.loading, deleteWeighting: null },
   };
  },

  // Get weighting pictures
  getWeightingPicturesStart(state, action: PayloadAction<number>) {
   return {
    ...state,
    loading: { ...state.loading, getWeightingPictures: true },
   };
  },
  getWeightingPicturesSuccess(
   state,
   action: PayloadAction<WeightingPicture[]>,
  ) {
   return {
    ...state,
    weightingPictures: action.payload,
    success: { ...state.success, getWeightingPictures: true },
    loading: { ...state.loading, getWeightingPictures: null },
   };
  },
  getWeightingPicturesFailure(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, getWeightingPictures: false },
    loading: { ...state.loading, getWeightingPictures: null },
   };
  },

  // Upload weighting pictures
  uploadWeightingPicturesStart(
   state,
   action: PayloadAction<{ weighingId: number; files: FileList }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, uploadWeightingPictures: true },
   };
  },
  uploadWeightingPicturesSuccess(
   state,
   action: PayloadAction<WeightingPicture[]>,
  ) {
   return {
    ...state,
    weightingPictures: action.payload,
    success: { ...state.success, uploadWeightingPictures: true },
    loading: { ...state.loading, uploadWeightingPictures: null },
   };
  },
  uploadWeightingPicturesFailure(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, uploadWeightingPictures: false },
    loading: { ...state.loading, uploadWeightingPictures: null },
   };
  },
  uploadWeightingPicturesFinish(state) {
   return {
    ...state,
    success: { ...state.success, uploadWeightingPictures: null },
    loading: { ...state.loading, uploadWeightingPictures: null },
   };
  },

  // Delete weighting picture
  deleteWeightingPictureStart(state, action: PayloadAction<number>) {
   return {
    ...state,
    loading: { ...state.loading, deleteWeightingPicture: true },
   };
  },
  deleteWeightingPictureSuccess(state, action: PayloadAction<number>) {
   return {
    ...state,
    weightingPictures: state.weightingPictures.filter(
     (p) => p.Id !== action.payload,
    ),
    success: { ...state.success, deleteWeightingPicture: true },
    loading: { ...state.loading, deleteWeightingPicture: null },
   };
  },
  deleteWeightingPictureFailure(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, deleteWeightingPicture: false },
    loading: { ...state.loading, deleteWeightingPicture: null },
   };
  },
 },
});

export const {
 getTableStart,
 getTableSuccess,
 getTableFailure,
 getTableFinish,
 getItemStart,
 getItemSuccess,
 getItemFailure,
 createItemStart,
 createItemSuccess,
 createItemFailure,
 createItemFinish,
 updateItemStart,
 updateItemSuccess,
 updateItemFailure,
 updateItemFinish,
 deleteItemStart,
 deleteItemSuccess,
 deleteItemFailure,
 deleteItemFinish,
 getTableStructureStart,
 getTableStructureSuccess,
 getTableStructureFailure,
 getItemDocumentsStart,
 getItemDocumentsSuccess,
 getItemDocumentsFailure,
 uploadItemDocumentsStart,
 uploadItemDocumentsSuccess,
 uploadItemDocumentsFailure,
 uploadItemDocumentsFinish,
 deleteItemDocumentStart,
 deleteItemDocumentSuccess,
 deleteItemDocumentFailure,
 deleteItemDocumentFinish,

 // Weighting specific actions
 getWeightingStart,
 getWeightingSuccess,
 getWeightingFailure,
 createWeightingStart,
 createWeightingSuccess,
 createWeightingFailure,
 updateWeightingStart,
 updateWeightingSuccess,
 updateWeightingFailure,
 deleteWeightingStart,
 deleteWeightingSuccess,
 deleteWeightingFailure,
 getWeightingPicturesStart,
 getWeightingPicturesSuccess,
 getWeightingPicturesFailure,
 uploadWeightingPicturesStart,
 uploadWeightingPicturesSuccess,
 uploadWeightingPicturesFailure,
 uploadWeightingPicturesFinish,
 deleteWeightingPictureStart,
 deleteWeightingPictureSuccess,
 deleteWeightingPictureFailure,
} = weightingsSlice.actions;

export default weightingsSlice;
